import React, { useState } from 'react';
import { CDN_URLS } from '../config/cdn';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import SpriteAnimatedLogo from '../ui/SpriteAnimatedLogo.js';
import { DEFAULT_ANIM_FRAME_RATE, GITHUB_URL, CONTACT_EMAIL, REQ_DEMO_EMAIL_SUBJECT, REQ_DEMO_EMAIL_BODY } from '../config/const.js';
import { SPRITES } from '../ui/SpriteAnimatedLogo.js';
import { createMailtoLink } from '../utils/email.js';

const Developers = () => {
  const [activeTab, setActiveTab] = useState('Python');

  // Create mailto link for demo request
  const demoEmailLink = createMailtoLink(CONTACT_EMAIL, REQ_DEMO_EMAIL_SUBJECT, REQ_DEMO_EMAIL_BODY);

  const integrations = [
    { name: 'OpenAI', icon: CDN_URLS.integrate.openai, status: 'active' },
    { name: 'Anthropic', icon: CDN_URLS.integrate.anthropic, status: 'active' },
    { name: 'Gemini', icon: CDN_URLS.integrate.gemini, status: 'active' },
    { name: 'DeepSeek', icon: CDN_URLS.integrate.deepseek, status: 'active' },
    { name: 'Qwen', icon: CDN_URLS.integrate.qwen, status: 'active' },
    { name: 'LangGraph', icon: CDN_URLS.integrate.langgraph, status: 'coming' },
    { name: 'CrewAI', icon: CDN_URLS.integrate.crewai, status: 'coming' },
    { name: 'SillyTavern', icon: CDN_URLS.integrate.sillytavern, status: 'active' },
    { name: 'N8N', icon: CDN_URLS.integrate.n8n, status: 'coming' },
    { name: 'Dify', icon: CDN_URLS.integrate.dify, status: 'coming' },
  ];

  const codeExamples = {
    Python: {
      language: 'python',
      code: `# Install memU SDK
pip install memu-py

# Initialize and use
from memu import MemuClient
import os

memu_client = MemuClient(
    api_key=os.getenv("MEMU_API_KEY")
)
memu_client.memorize_conversation(
    conversation=conversation,
    user_name="User",
    agent_name="Assistant"
)`
    },
    JavaScript: {
      language: 'javascript',
      code: `// Install memU SDK
npm install memu-js

// Initialize and use
import { MemuClient } from 'memu-js';

const memuClient = new MemuClient({
    apiKey: process.env.MEMU_API_KEY
});

await memuClient.memorizeConversation({
    conversationText: conversation,
    userName: "User",
    agentName: "Assistant"
});`
    },
    'REST API': {
      language: 'bash',
      code: `# Store conversation via REST API
curl -X POST "https://api.memu.ai/v1/conversations" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "conversation": "User: Hello\\nAssistant: Hi there!",
    "user_name": "User",
    "agent_name": "Assistant"
  }'`
    }
  };

  return (
    <section className="developers">
      <div className="dev-hero">
        <div className="dev-hero-content">
          <div className="dev-text-section">
            <div className="dev-header">
              For Developers
            </div>
            <h1 className="dev-main-title">
              Integrate into your LLM apps
            </h1>

            <div className="dev-actions">
              <a href={GITHUB_URL} className="btn btn-primary btn-lg" target="_blank" rel="noopener noreferrer">
                Check our Github
              </a>
              <a href={demoEmailLink} className="btn btn-outline btn-lg">
                Request a demo →
              </a>
            </div>

            <div className="dev-integrations">
              <h3>Integrates with</h3>
              <div className="integration-grid">
                {integrations.map((integration, index) => (
                  <div key={index} className={`integration-item ${integration.status}`}>
                    <img
                      src={integration.icon}
                      alt={integration.name}
                      className="integration-icon"
                    />
                    <span className="integration-name">{integration.name}</span>
                    {integration.status === 'coming' && (
                      <span className="coming-soon">Coming soon</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="dev-preview-section">
            <div className="dev-preview-window">
              <div className="preview-header">
                <div className="preview-tabs">
                  {Object.keys(codeExamples).map((tab) => (
                    <span
                      key={tab}
                      className={`tab ${activeTab === tab ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab}
                    </span>
                  ))}
                </div>
              </div>
              <div className="preview-content">
                <div className="code-container">
                  <SyntaxHighlighter
                    language={codeExamples[activeTab].language}
                    style={prism}
                    customStyle={{
                      margin: 0,
                      padding: '16px',
                      background: 'white',
                      fontSize: '13px',
                      lineHeight: '1.5', border: '1px solid #e5e7eb', borderRadius: '8px',
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                    showLineNumbers={false}
                    wrapLines={true}
                    wrapLongLines={true}
                    PreTag="div"
                    CodeTag="div"
                  >
                    {codeExamples[activeTab].code}
                  </SyntaxHighlighter>
                </div>

                <div className="dev-anim1">
                  <SpriteAnimatedLogo
                    width={80}
                    height={80}
                    frameRate={DEFAULT_ANIM_FRAME_RATE}
                    autoPlay={true}
                    filename={SPRITES.ANIM_PROBE_RIGHT} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Developers;